import { AlertTriangle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { EmptyState } from '@/components/EmptyState';
import { InitialsAvatar } from '@/components/InitialsAvatar';
import { Badge } from '@/components/ui/badge';
import { SegmentedControl, type SegmentedOption } from '@/components/ui/segmented-control';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAdminUsers } from '@/hooks/use-users';
import { formatDateTime, formatLocalDate } from '@/lib/format';
import type { AdminUser, UserStatusFilter } from '@/types/users';
import { UserActionsMenu } from './UserActionsMenu';

const FILTERS: ReadonlyArray<SegmentedOption<UserStatusFilter>> = [
  { value: 'active', label: 'Active' },
  { value: 'deactivated', label: 'Deactivated' },
  { value: 'all', label: 'All' },
];

function parseFilter(raw: string | null): UserStatusFilter {
  return raw === 'deactivated' || raw === 'all' ? raw : 'active';
}

function RoleBadge({ role }: { role: AdminUser['role'] }): JSX.Element {
  return <Badge variant="outline">{role === 'admin' ? 'Admin' : 'User'}</Badge>;
}

function StatusBadge({ status }: { status: AdminUser['status'] }): JSX.Element {
  return <Badge variant={status === 'active' ? 'success' : 'outline'}>{status === 'active' ? 'Active' : 'Deactivated'}</Badge>;
}

function SyncBadge({ user }: { user: AdminUser }): JSX.Element | null {
  if (!user.accessSyncIssue) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <Badge variant="warning">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            Access sync pending
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent>Workspace sign-in access couldn&apos;t be updated ({user.accessSyncIssue.operation}).</TooltipContent>
    </Tooltip>
  );
}

function NameCell({ user }: { user: AdminUser }): JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <InitialsAvatar name={user.name} email={user.email} />
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate font-medium">{user.name}</span>
          {user.isSelf && <Badge variant="info">You</Badge>}
          <SyncBadge user={user} />
        </div>
      </div>
    </div>
  );
}

function lastActivity(user: AdminUser): string {
  return user.lastLoginAt ? formatDateTime(user.lastLoginAt) : 'Never';
}

export function UsersTab(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const status = parseFilter(searchParams.get('status'));
  const { data, isLoading } = useAdminUsers(status);

  function setStatus(next: UserStatusFilter): void {
    const params = new URLSearchParams(searchParams);
    params.set('status', next);
    setSearchParams(params, { replace: true });
  }

  const users = data?.items ?? [];
  const activeAdminCount = data?.meta.activeAdminCount ?? 0;

  return (
    <div className="space-y-4">
      <SegmentedControl aria-label="Status filter" value={status} onChange={setStatus} options={FILTERS} />

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : users.length === 0 ? (
        <EmptyState title="No users to show." />
      ) : (
        <>
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Start date</TableHead>
                  <TableHead>Last activity</TableHead>
                  <TableHead className="w-12">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                    <TableCell>
                      <NameCell user={user} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">{user.email}</TableCell>
                    <TableCell>
                      <RoleBadge role={user.role} />
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={user.status} />
                    </TableCell>
                    <TableCell className="font-mono tabular-nums">{formatLocalDate(user.startDate)}</TableCell>
                    <TableCell className="font-mono tabular-nums">{lastActivity(user)}</TableCell>
                    <TableCell>
                      <UserActionsMenu user={user} activeAdminCount={activeAdminCount} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ul className="space-y-3 md:hidden">
            {users.map((user) => (
              <li key={user.id} className="card p-3">
                <div className="flex items-start justify-between gap-2">
                  <NameCell user={user} />
                  <UserActionsMenu user={user} activeAdminCount={activeAdminCount} />
                </div>
                <p className="mt-2 truncate text-sm text-muted-foreground">{user.email}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <RoleBadge role={user.role} />
                  <StatusBadge status={user.status} />
                </div>
                <dl className="mt-2 grid grid-cols-2 gap-1 text-xs">
                  <dt className="text-muted-foreground">Start date</dt>
                  <dd className="font-mono tabular-nums">{formatLocalDate(user.startDate)}</dd>
                  <dt className="text-muted-foreground">Last activity</dt>
                  <dd className="font-mono tabular-nums">{lastActivity(user)}</dd>
                </dl>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
